import React from 'react';

import _ from 'es-toolkit/compat';
import { useDebouncedCallback } from 'use-debounce';

import { Field, Form, FormApi, FormSpy, useForm } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';
import { noop } from '@clearverify/utils';

import { SelectField, TextField } from 'src/components/finalForm';
import type { SelectFieldOption } from 'src/types/components';
import { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

import {
  DEFAULT_DEBOUNCE,
  TABLE_SEARCH_FORM_INITIAL_VALUES,
  TableSearchFormField,
  TableSearchFormValues,
} from './constants';

interface TableSearchProps {
  onSearch?: (value: Nullable<string>) => void;
  onCategorySelect?: (category: Nullable<string>) => void;
  searchStyles?: SxProp;
  searchFieldStyles?: SxProp;
  searchCategories?: SelectFieldOption[];
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  size?: 'small' | 'medium';
}

export const TableSearch: React.FC<TableSearchProps> = ({
  onSearch,
  onCategorySelect,
  searchStyles,
  searchFieldStyles,
  searchCategories,
  disabled = false,
  placeholder,
  label = 'Search',
  size = 'medium',
}) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: reports the preselected category once on mount; later changes go through handleChange
  React.useEffect(() => {
    if (searchCategories?.[0]?.value) {
      onCategorySelect?.(searchCategories[0].value);
    }
  }, []);

  const debouncedSearch = useDebouncedCallback((value: Nullable<string>) => {
    onSearch?.(value);
  }, DEFAULT_DEBOUNCE);

  const searchComposedStyles = React.useMemo(() => {
    return composeSx(
      baseFormStyles,
      { width: searchCategories && !_.isEmpty(searchCategories) ? 400 : 300 },
      searchStyles,
    );
  }, [searchCategories, searchStyles]);

  const initialValues = React.useMemo(() => {
    if (searchCategories?.[0]?.value) {
      return { ...TABLE_SEARCH_FORM_INITIAL_VALUES, searchCategory: searchCategories[0].value };
    }
    return TABLE_SEARCH_FORM_INITIAL_VALUES;
  }, [searchCategories]);

  const cachedFormValues = React.useRef(initialValues);

  const handleChange = React.useCallback(
    (formApi: FormApi<TableSearchFormValues>, formValues: TableSearchFormValues) => {
      if (cachedFormValues.current.searchValue !== formValues.searchValue) {
        debouncedSearch(formValues.searchValue);
      }

      if (
        formValues.searchCategory &&
        cachedFormValues.current.searchCategory !== formValues.searchCategory
      ) {
        onCategorySelect?.(formValues.searchCategory);
        if (!_.isNil(formValues.searchValue)) {
          formApi.change(TableSearchFormField.searchValue, null);
        }
      }

      cachedFormValues.current = formValues;
    },
    [debouncedSearch, onCategorySelect],
  );

  return (
    <Form<TableSearchFormValues>
      initialValues={initialValues}
      onSubmit={noop}
      containerProps={{ sx: searchComposedStyles }}
    >
      {() => (
        <React.Fragment>
          {searchCategories && !_.isEmpty(searchCategories) && (
            <Field
              name={TableSearchFormField.searchCategory}
              component={SelectField}
              label="Search by"
              options={searchCategories}
              fullWidth
              size={size}
              disabled={disabled}
            />
          )}

          <Field
            name={TableSearchFormField.searchValue}
            component={TextField}
            label={label}
            placeholder={placeholder}
            sx={composeSx(baseTextFieldStyles, searchFieldStyles)}
            fullWidth
            size={size}
            disabled={disabled}
          />

          <TableSearchFormSpy onChange={handleChange} />
        </React.Fragment>
      )}
    </Form>
  );
};

type TableSearchFormSpyProps = {
  onChange: (formApi: FormApi<TableSearchFormValues>, values: TableSearchFormValues) => void;
};

// lives inside <Form> so it can reach the form api through context instead of a per-render closure
const TableSearchFormSpy: React.FC<TableSearchFormSpyProps> = ({ onChange }) => {
  const form = useForm<TableSearchFormValues>();
  const handleChange = React.useCallback(
    ({ values }: { values: TableSearchFormValues }) => onChange(form, values),
    [form, onChange],
  );

  return <FormSpy<TableSearchFormValues> subscription={{ values: true }} onChange={handleChange} />;
};

const baseFormStyles: SxProp = theme => ({
  display: 'flex',
  gap: 2,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    '& > *': {
      width: '100%',
    },
  },
});

const baseTextFieldStyles: SxProp = theme => ({
  '&:has(input:disabled)': {
    backgroundColor: theme.palette.grey[200],
  },
});
